import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EntityConstants,
  EntityOrderBy,
  GenericEntity,
  IEntityPermissions,
  IGenericRequestParameters,
  InferSortByOptions,
  SortDirection,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import { UseTabActions } from "../Tabs/TableTabsTypes";
import {
  GenericVirtualizedTableCell,
  GenericVirtualizedTableCells,
  GenericVirtualizedTableFunctionRef,
  SortState,
} from "./GenericVirtualizedTableTypes";
import { useDebouncedValue } from "../../helperfunctions/useDebouncedValue";
import { ColumnsSettings } from "../ColumnsSelector/ColumnsSelector";
import {
  EntityByEntityTypeId,
  FiltersByEntityTypeId,
  GenericEntityConstant,
  GenericEntityConstantsEntities,
} from "../../../api/GenericConstants";

export interface UseEntityTableDefaultProps<EntityTypeId extends GenericEntityConstantsEntities> {
  fieldLabels: GenericEntityConstant<EntityTypeId>["fieldLabels"];
}
export interface UseEntityTableProps<
  EntityTypeId extends GenericEntityConstantsEntities,
> extends UseEntityTableDefaultProps<EntityTypeId> {
  entityConstants: EntityConstants<EntityByEntityTypeId<EntityTypeId>, FiltersByEntityTypeId<EntityTypeId>>;
  sort: SortState<InferSortByOptions<FiltersByEntityTypeId<EntityTypeId>>>[];
  setSort: (newSort: SortState<InferSortByOptions<FiltersByEntityTypeId<EntityTypeId>>>[]) => Promise<void>;
}

export interface UseITypedEntityTableProps<
  EntityTypeId extends GenericEntityConstantsEntities,
> extends UseEntityTableProps<EntityTypeId> {
  defaults: ColumnsSettings<EntityByEntityTypeId<EntityTypeId>>;
  filters: FiltersByEntityTypeId<EntityTypeId>;
}

interface UseEntityTableColumnsReturn<EntityTypeId extends GenericEntityConstantsEntities> {
  columns: GenericVirtualizedTableCells<EntityByEntityTypeId<EntityTypeId>>;
  columnSettings?: ColumnsSettings<EntityByEntityTypeId<EntityTypeId>>;
}

export type UseEntityTableColumns<EntityTypeId extends GenericEntityConstantsEntities> = (
  props: UseEntityTableProps<EntityTypeId>
) => UseEntityTableColumnsReturn<EntityTypeId>;

/**
 * Custom hook to manage the state of a generic virtualized table.
 */
export const useGenericVirtualizedTable = <Entity extends GenericEntity>() => {
  const [selection, setSelection] = useState<Set<Entity["id"]>>(new Set()); // Selected elements in the table will be held here
  const [resultsCount, setResultsCount] = useState<number>();
  const [selectionPermissions, setSelectionPermissions] = useState<IEntityPermissions>();

  const onCountChange = useCallback((count: number | undefined) => {
    setResultsCount(count);
  }, []);

  const onSelectionChange = useCallback((selection: Set<Entity["id"]>) => {
    setSelection(selection);
  }, []);

  const onSelectionPermissions = useCallback((permissions?: IEntityPermissions) => {
    setSelectionPermissions(permissions);
  }, []);

  return {
    selection,
    resultsCount,
    selectionPermissions,
    onCountChange,
    onSelectionChange,
    onSelectionPermissions,
  };
};

const compareSortStates = (
  sortA: EntityOrderBy<any>[] | null | undefined,
  sortB: SortState<any>[] | null | undefined
): boolean => {
  if (sortA === sortB) return true;
  if ((!sortA || !sortA.length) && (!sortB || !sortB.length)) return true;
  if (!sortA || !sortA.length || !sortB || !sortB.length) return false;
  if (sortA!.length !== sortB!.length) return false;
  for (let i = 0; i < sortA!.length; i++) {
    if (sortA![i].property !== sortB![i].property || sortA![i].sortDirection !== sortB![i].sortDirection) {
      return false;
    }
  }
  return true;
};

/**
 * Custom hook to manage the state of a generic virtualized table tabs.
 */
interface UseGenericVirtualizedTableTabsProps<
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
  FilterForm extends StringIndexedDict,
> {
  currentTab: string;
  tabsLoading: boolean;
  filters: Filters;
  switchSortState: (
    sortState: EntityOrderBy<InferSortByOptions<Filters>>[] | null | undefined
  ) => SortState<InferSortByOptions<Filters>>[];
  dispatchTabStore: (action: UseTabActions<Entity, Filters, FilterForm>) => Promise<void>;
}
export const useGenericVirtualizedTableTabs = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
  FilterForm extends StringIndexedDict,
>({
  currentTab,
  tabsLoading,
  filters,
  switchSortState,
  dispatchTabStore,
}: UseGenericVirtualizedTableTabsProps<Entity, Filters, FilterForm>) => {
  const functionRef = useRef<GenericVirtualizedTableFunctionRef<Entity>>(null);
  const [sort, setSort] = useState<SortState<InferSortByOptions<Filters>>[]>([]);
  const [searchValue, setSearchValue] = useState(""); //SearchTerm
  const debouncedSearchValue = useDebouncedValue(searchValue, 300); // Debounced searchTerm used for filtering
  const memoizedsearchTerm = useMemo(() => filters.searchTerm ?? "", [filters.searchTerm]);
  const memoizedSortBy = useMemo(() => filters.sortBy, [filters.sortBy]);
  const synchedDebouncedSearchValue = useRef(false);
  const initOnSort = useRef(false);
  const initSearchTerm = useRef(false);

  const memoizedCurrentTabRef = useRef<string>();

  const onSort = useCallback(
    async (newSort: SortState<InferSortByOptions<Filters>>[]) => {
      if (!tabsLoading) {
        setSort(newSort);
        await dispatchTabStore({
          type: "setTab",
          payload: {
            settings: {
              filters: {
                sortBy:
                  newSort?.map((sort) => ({
                    property: sort.property,
                    sortDirection: sort.sortDirection,
                    ...(!!sort.customFieldId && { customFieldId: sort.customFieldId }),
                  })) ?? ([] as Filters["sortBy"]),
              } as Filters,
            },
          },
          options: { keepPrevious: true },
        });
        functionRef.current?.resetSelection();
      }
    },
    [dispatchTabStore, tabsLoading]
  );

  const onTabChange = useCallback(
    async (currentTabId: string) => {
      if (!tabsLoading) {
        await dispatchTabStore({ type: "setCurrentTab", tabId: currentTabId });
        synchedDebouncedSearchValue.current = false;
        functionRef.current?.resetSelection();
      }
    },
    [dispatchTabStore, tabsLoading]
  );

  useEffect(() => {
    if (currentTab === memoizedCurrentTabRef.current) {
      if (debouncedSearchValue === searchValue) {
        synchedDebouncedSearchValue.current = true;
      } else {
        synchedDebouncedSearchValue.current = false;
      }
    } else if (currentTab !== memoizedCurrentTabRef.current) {
      memoizedCurrentTabRef.current = currentTab;
      synchedDebouncedSearchValue.current = false;
      setSearchValue(memoizedsearchTerm);
    }

    if (!tabsLoading && synchedDebouncedSearchValue.current) {
      (async () =>
        await dispatchTabStore({
          type: "setTab",
          payload: { settings: { filters: { searchTerm: debouncedSearchValue } as Filters } },
          options: { keepPrevious: true },
        }))();
    }
  }, [currentTab, debouncedSearchValue, dispatchTabStore, memoizedsearchTerm, searchValue, tabsLoading]);

  useEffect(() => {
    if (memoizedSortBy) {
      const newState = switchSortState(memoizedSortBy);
      if (!compareSortStates(memoizedSortBy, sort)) {
        setSort(newState);
        if (!tabsLoading && !initOnSort.current) {
          (async () => await onSort(newState))();
          initOnSort.current = true;
        }
      }
    }
    if (!tabsLoading && !initSearchTerm.current && memoizedsearchTerm) {
      setSearchValue(memoizedsearchTerm);
      initSearchTerm.current = true;
    }
  }, [memoizedSortBy, sort, switchSortState, onSort, tabsLoading, memoizedsearchTerm]);

  // console.log(
  //   "currentTab:",
  //   currentTab,
  //   "memoizedCurrentTabRef",
  //   memoizedCurrentTabRef.current,
  //   "memoizedsearchTerm:",
  //   memoizedsearchTerm,
  //   "searchValue:",
  //   searchValue,
  //   "debounced:",
  //   debouncedSearchValue,
  //   "synched:",
  //   synchedDebouncedSearchValue.current
  // );

  return { functionRef, onTabChange, sort, setSort: onSort, searchValue, setSearchValue, debouncedSearchValue };
};

export const getSortStateArray = <SortBy extends string>(
  sortState: EntityOrderBy<SortBy>[] | null | undefined,
  expression: (sortBy: EntityOrderBy<SortBy>) => SortState<SortBy>
): SortState<SortBy>[] => {
  let _sortState: SortState<SortBy>[] = [];
  if (!sortState || !Array.isArray(sortState) || (Array.isArray(sortState) && !sortState.length)) return _sortState;
  for (const sort of sortState) {
    const s = expression(sort);
    _sortState.push({
      headerId: s.headerId,
      sortDirection: s.sortDirection,
      property: s.property,
    });
  }
  return _sortState;
};

export const genericDefaultSortState = <SortBy extends string>(
  sortState: EntityOrderBy<SortBy>[] | null | undefined
): SortState<SortBy>[] | null => {
  if (!sortState || !Array.isArray(sortState) || (Array.isArray(sortState) && !sortState.length)) return null;
  let _sortState: SortState<SortBy>[] = [];
  for (const sort of sortState) {
    switch (sort.property) {
      case "ID":
        _sortState.push({
          headerId: "default-id",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "NAME":
        _sortState.push({
          headerId: "default-name",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "ENTERED_ON":
        _sortState.push({
          headerId: "default-enteredOn",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "ENTERED_BY":
        _sortState.push({
          headerId: "default-enteredBy",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "MODIFIED_ON":
        _sortState.push({
          headerId: "default-modifiedOn",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "MODIFIED_BY":
        _sortState.push({
          headerId: "default-modifiedBy",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "CUSTOM_FIELD":
        _sortState.push({
          headerId: `${sort.customFieldId}`,
          sortDirection: sort.sortDirection,
          property: sort.property,
          customFieldId: sort.customFieldId,
        });
        break;
      case "CUSTOM_TYPE":
        _sortState.push({
          headerId: "default-customType",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      case "OWNER":
        _sortState.push({
          headerId: "default-owner",
          sortDirection: sort.sortDirection,
          property: sort.property,
        });
        break;
      default:
        break;
    }
  }
  return Array.isArray(_sortState) && !!_sortState.length ? _sortState : null;
};

export const getAsyncSortingFn = <SortByOptions extends string>(
  property: SortByOptions,
  sort: SortState<SortByOptions>[],
  setSort: (newSort: SortState<SortByOptions>[]) => Promise<void>
): Omit<GenericVirtualizedTableCell<any>, "id" | "Header" | "accessor" | "width"> => {
  return {
    clearSort: async (id: string) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
        const newSort = [...sort];
        newSort.splice(currentSortIndex, 1);
        await setSort(newSort);
      }
    },
    sortingFn: async (id: string, allowMultiSort: boolean) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (allowMultiSort) {
        if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
          if (sort[currentSortIndex].sortDirection === "ASC") {
            const newSort = [...sort];
            newSort[currentSortIndex] = {
              headerId: id,
              sortDirection: "DESC",
              property: property,
            };
            await setSort(newSort);
          } else {
            const newSort = [...sort];
            newSort[currentSortIndex] = {
              headerId: id,
              sortDirection: "ASC",
              property: property,
            };
            // newSort.splice(currentSortIndex, 1);
            await setSort(newSort);
          }
        } else {
          await setSort([
            ...sort,
            {
              headerId: id,
              sortDirection: "ASC",
              property: property,
            },
          ]);
        }
      } else {
        if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
          if (sort[currentSortIndex].sortDirection === "ASC") {
            await setSort([
              {
                headerId: id,
                sortDirection: "DESC",
                property: property,
              },
            ]);
          } else {
            await setSort([
              {
                headerId: id,
                sortDirection: "ASC",
                property: property,
              },
            ]);
          }
        } else {
          await setSort([
            {
              headerId: id,
              sortDirection: "ASC",
              property: property,
            },
          ]);
        }
      }
    },
    sortDirection: (id: string) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
        return sort[currentSortIndex].sortDirection;
      }
      return undefined;
    },
    sortIndex: (id: string) => {
      if (Array.isArray(sort) && !!sort.length) {
        const currentSortIndex = sort.findIndex((s) => s.headerId === id);
        return currentSortIndex >= 0 ? currentSortIndex + 1 : undefined;
      }
      return undefined;
    },
  };
};

export const getSortingFn = <SortByOptions extends string, Filters extends StringIndexedDict>(
  property: SortByOptions,
  sort: SortState<SortByOptions>[],
  setSort: React.Dispatch<React.SetStateAction<SortState<SortByOptions>[]>>,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
) => {
  return {
    clearSort: (id: string) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
        const newSort = [...sort];
        newSort.splice(currentSortIndex, 1);
        setSort(newSort);
        setFilters((prev) => ({ ...prev, sortBy: newSort as Filters["sortBy"] }));
      }
    },
    sortingFn: (id: string, allowMultiSort: boolean) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (allowMultiSort) {
        if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
          if (sort[currentSortIndex].sortDirection === "ASC") {
            const newSort = [...sort];
            newSort[currentSortIndex] = {
              headerId: id,
              sortDirection: "DESC",
              property: property,
            };
            setSort(newSort);
            setFilters((prev) => ({ ...prev, sortBy: newSort as Filters["sortBy"] }));
          } else {
            const newSort = [...sort];
            newSort[currentSortIndex] = {
              headerId: id,
              sortDirection: "ASC",
              property: property,
            };
            setSort(newSort);
            setFilters((prev) => ({ ...prev, sortBy: newSort as Filters["sortBy"] }));
          }
        } else {
          setSort([
            ...sort,
            {
              headerId: id,
              sortDirection: "ASC",
              property: property,
            },
          ]);
          setFilters((prev) => ({
            ...prev,
            sortBy: [
              ...(Array.isArray(prev.sortBy) ? prev.sortBy : []),
              {
                property: property,
                sortDirection: "ASC",
              },
            ] as Filters["sortBy"],
          }));
        }
      } else {
        if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
          if (sort[currentSortIndex].sortDirection === "ASC") {
            const newSort = [
              {
                headerId: id,
                sortDirection: "DESC" as SortDirection,
                property: property,
              },
            ];
            setSort(newSort);
            setFilters((prev) => ({ ...prev, sortBy: newSort as Filters["sortBy"] }));
          } else {
            const newSort = [
              {
                headerId: id,
                sortDirection: "ASC" as SortDirection,
                property: property,
              },
            ];
            setSort(newSort);
            setFilters((prev) => ({ ...prev, sortBy: newSort as Filters["sortBy"] }));
          }
        } else {
          setSort([
            {
              headerId: id,
              sortDirection: "ASC",
              property: property,
            },
          ]);
          setFilters((prev) => ({
            ...prev,
            sortBy: [
              {
                property: property,
                sortDirection: "ASC",
              },
            ] as Filters["sortBy"],
          }));
        }
      }
    },
    sortDirection: (id: string) => {
      const currentSortIndex = Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
      if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
        return sort[currentSortIndex].sortDirection;
      }
      return undefined;
    },
    sortIndex: (id: string) => {
      if (Array.isArray(sort) && !!sort.length) {
        const currentSortIndex = sort.findIndex((s) => s.headerId === id);
        return currentSortIndex >= 0 ? currentSortIndex + 1 : undefined;
      }
      return undefined;
    },
  };
};
